import { PartyRelationshipTypeCode, PartyToEmployeeRelationDto, PartyToPartyRelationDto } from 'types/contacts';
import { NamedEntityMinDto } from 'types/other';
import { sortByField } from 'utils/sort';

const reduceEmployees = (
    relations: PartyToEmployeeRelationDto[] | null,
    partyEmployeeTypeCode: string
): NamedEntityMinDto[] => {
    const employees = relations?.reduce<NamedEntityMinDto[]>((acc, val) => {
        if (val.partyEmployeeTypeCode === partyEmployeeTypeCode && val.employee.name) {
            // Carry first/last through so the selected chip can flip to "Last First" (LAST_NAME_FIRST).
            acc.push({
                id: val.employee.id,
                name: val.employee.name,
                firstName: val.employee.firstName,
                lastName: val.employee.lastName,
            });
        }
        return acc;
    }, []);

    return sortByField(employees ?? [], 'name');
};

const reduceContacts = (
    relations: PartyToPartyRelationDto[] | null,
    partyRelationshipTypeCode: string
): { id: number; name: string }[] => {
    const contacts = relations?.reduce<{ id: number; name: string }[]>((acc, val) => {
        if (val.partyRelationshipTypeCode === partyRelationshipTypeCode && val.otherParty.name) {
            acc.push({ id: val.otherParty.id, name: val.otherParty.name });
        }
        return acc;
    }, []);
    return sortByField(contacts ?? [], 'name');
};

export const getSecondaryRecipientContacts = (relations: PartyToPartyRelationDto[] | null) => {
    return (relations || [])
        .filter((val) => val.partyRelationshipTypeCode === PartyRelationshipTypeCode.SECONDARY_RECIPIENT)
        .map((item) => item.otherParty);
};

// Find and map primary contact
export const getPrimaryContact = (relations: PartyToPartyRelationDto[] | null) => {
    const primaryContact = relations?.find(
        (val) => val.partyRelationshipTypeCode === PartyRelationshipTypeCode.PRIMARY_CONTACT
    );
    return primaryContact ? { id: primaryContact.otherParty.id, name: primaryContact.otherParty.name } : null;
};

export const getEmployees = (relations: PartyToPartyRelationDto[] | null) => {
    return reduceContacts(relations, 'EMPLOYEE');
};

export const getEmployedBy = (relatedTo: PartyToPartyRelationDto[] | null) => {
    return reduceContacts(relatedTo, 'EMPLOYEE');
};

export const getManagers = (relations: PartyToEmployeeRelationDto[] | null): NamedEntityMinDto[] => {
    return reduceEmployees(relations, 'RELATIONSHIP_MANAGER');
};

export const getPartners = (relations: PartyToEmployeeRelationDto[] | null): NamedEntityMinDto[] => {
    return reduceEmployees(relations, 'RELATIONSHIP_PARTNER');
};

export const getAdministrators = (relations: PartyToEmployeeRelationDto[] | null): NamedEntityMinDto[] => {
    return reduceEmployees(relations, 'PARTY_ADMINISTRATOR');
};

export const getBusinessContacts = (relations: PartyToEmployeeRelationDto[] | null): NamedEntityMinDto[] => {
    return reduceEmployees(relations, 'BUSINESS_CONTACT_OF');
};

export const getPrimaryContactForCompanies = (relations: PartyToPartyRelationDto[] | null) => {
    return reduceContacts(relations, 'PRIMARY_CONTACT');
};

export const getPaymentNotificationReceivers = (
    relations: PartyToEmployeeRelationDto[] | null
): NamedEntityMinDto[] => {
    return (
        relations?.map((item) => ({
            id: item.employee.id,
            name: item.employee.name,
            firstName: item.employee.firstName,
            lastName: item.employee.lastName,
        })) ?? []
    );
};
